import * as React from "react";
import {
  AriaModalOverlayProps,
  Overlay as AriaOverlay,
  useDialog,
  useModalOverlay,
} from "react-aria";
import {
  OverlayTriggerProps,
  OverlayTriggerState,
  useOverlayTriggerState,
} from "react-stately";
import { Flex } from "../flex";
import { Text } from "../text";
import { Button } from "../button";
import { HiX } from "react-icons/hi";
import { StyleProps } from "../../utils/types";
import { cx } from "../../utils/dom";

export interface ModalProps
  extends AriaModalOverlayProps,
    StyleProps<
      | "wrapper"
      | "underlay"
      | "content"
      | "header"
      | "body"
      | "footer"
      | "title"
    > {
  hideTitle?: boolean;
  state?: OverlayTriggerState;
  children?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  onConfirm?(): void;
  onCancel?(): void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonProps?: React.ComponentProps<typeof Button>;
  cancelButtonProps?: React.ComponentProps<typeof Button>;
  width?: number | string;
  variant?: "modal" | "drawer";
  showCloseButton?: boolean;
  onOpenChange?(open: boolean): void;
  onClose?(): void;
  isOpen?: boolean;
  defaultOpen?: boolean;
}

export function Overlay({
  children,
  state,
  classNames,
  styles,
  variant = "modal",
  isDismissable = true,
  ...props
}: ModalProps) {
  const modalRef = React.useRef(null);

  const { modalProps, underlayProps } = useModalOverlay(
    { ...props, isDismissable },
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    state!,
    modalRef
  );

  if (!state?.isOpen) {
    return null;
  }
  return (
    <AriaOverlay>
      <div
        style={styles?.underlay}
        className={cx(["p-modal-underlay", classNames?.underlay])}
        {...underlayProps}
      >
        <div
          data-variant={variant}
          style={styles?.wrapper}
          className={cx(["p-modal-container", classNames?.wrapper])}
          {...modalProps}
          ref={modalRef}
        >
          {children}
        </div>
      </div>
    </AriaOverlay>
  );
}

function Dialog({
  width = 400,
  title,
  onCancel,
  onConfirm,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  description,
  children,
  confirmButtonProps,
  cancelButtonProps,
  showCloseButton = true,
  onClose,
  className,
  style,
  hideTitle,
  classNames,
  styles,
  state,
  ...props
}: ModalProps) {
  const dialogRef = React.useRef(null);

  const { dialogProps, titleProps } = useDialog(
    { role: "dialog", ...props },
    dialogRef
  );

  function handleClose() {
    state?.close();
    onClose?.();
  }

  return (
    <div
      data-radius="md"
      className={cx("p-modal-content", className, classNames?.content)}
      style={{ width: width, ...style, ...styles?.content }}
      ref={dialogRef}
      {...dialogProps}
    >
      {!hideTitle && (
        <Flex
          align="center"
          as="header"
          justify="space-between"
          gap="2xl"
          style={styles?.header}
          className={cx(["p-modal-head", classNames?.header])}
        >
          <Flex stack gap={"3xs"}>
            {title && (
              <Text
                style={styles?.title}
                className={cx(["p-modal-title", classNames?.title])}
                weight={"medium"}
                size={"md"}
                color={"black"}
                as="div"
                {...titleProps}
              >
                {title}
              </Text>
            )}
            {description && (
              <Text lineHeight={"base"} size={"xs"} variant={"light"} as="div">
                {description}
              </Text>
            )}
          </Flex>

          {showCloseButton && (
            <button
              color="gray"
              onClick={handleClose}
              data-variant="ghost"
              data-color="gray"
              className="p-button size-sm"
              data-radius={"full"}
              data-icon-button={true}
            >
              <HiX size={18} />
            </button>
          )}
        </Flex>
      )}
      <Flex
        stack
        className={cx(["p-modal-body", classNames?.body])}
        style={styles?.body}
      >
        {children}
      </Flex>

      {(onConfirm || onCancel) && (
        <Flex
          as="footer"
          style={styles?.footer}
          className={cx(["p-modal-footer", classNames?.footer])}
          gap={"xs"}
          justify={"end"}
          stretchX
        >
          {onCancel && (
            <Button
              onPress={handleClose}
              variant={"outline"}
              color={"gray"}
              {...cancelButtonProps}
            >
              {cancelLabel}
            </Button>
          )}
          {onConfirm && (
            <Button onPress={onConfirm} {...confirmButtonProps}>
              {confirmLabel}
            </Button>
          )}
        </Flex>
      )}
    </div>
  );
}

export function Modal({
  isOpen,
  defaultOpen,
  onOpenChange,
  ...props
}: Omit<ModalProps, "state">) {
  const state = useOverlayTriggerState({ defaultOpen, isOpen, onOpenChange });

  return (
    <Overlay state={state} {...props}>
      <Dialog onOpenChange={onOpenChange} state={state} {...props} />
    </Overlay>
  );
}

export function useModal(props: OverlayTriggerProps = {}) {
  const state = useOverlayTriggerState(props);
  return state;
}

Modal.useModal = useModal;
